import { useCallback } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { store } from 'store/store';
import { axiosInstance } from 'services/axiosBaseInstance';
import { setToasterMessage } from 'store/globalSlice';
import { QUERIES_KEYS } from 'constant';
import { clearMerge } from '../slice';
import useTexts from './useTexts';

const useHandleMerge = (
  setLoading: (loading: boolean) => void,
  handleCloseMergeModal: () => void,
  setShowWarningPrompt: (show: boolean) => void,
): (() => Promise<void>) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { serviceRecordMergedPart1, serviceRecordMergedPart2, serviceRecordMergeFailed } = useTexts();

  const handleMerge = useCallback(async () => {
    setLoading(true);
    const storeState = store.getState();
    const mergeRequestsStore = storeState.mergeRequests;

    const { primarySrId, mergeOptions, mergeStatus, mergeReason } = mergeRequestsStore;
    // @ts-ignore
    const selectedTickets = storeState?.queue?.selectedTickets;
    const mergedIds = selectedTickets.reduce((acc, e) => [...acc, ...(e.id !== primarySrId ? [e.id] : [])], []);

    const postData = {
      primaryId: primarySrId,
      mergedIds,
      mergeReason: mergeReason?.trim(),
      mergeStatus: mergeStatus?.id,
      ...mergeOptions,
    };

    try {
      await axiosInstance.patch('sr/merge', postData);
      setLoading(false);
      [primarySrId, ...mergedIds].forEach((id) => {
        queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.SR_ID, String(id)], exact: true });
      });
      queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.QUEUE_DATA], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERIES_KEYS.SR_ATTACHMENTS, primarySrId], exact: true });
      router.navigate({ to: '/spaces/ticket', search: { id: primarySrId } });
      dispatch(
        setToasterMessage({
          message: `${serviceRecordMergedPart1} #${primarySrId} ${serviceRecordMergedPart2}`,
          showFor: 5000,
        }),
      );
      dispatch(clearMerge());
    } catch (e) {
      console.error(e);
      dispatch(
        setToasterMessage({
          type: 'error',
          message: serviceRecordMergeFailed,
          showFor: 5000,
        }),
      );
      setLoading(false);
      handleCloseMergeModal();
      setShowWarningPrompt(false);
    }
  }, [
    router,
    serviceRecordMergedPart1,
    serviceRecordMergedPart2,
    serviceRecordMergeFailed,
    handleCloseMergeModal,
    setShowWarningPrompt,
    dispatch,
    queryClient,
    setLoading,
  ]);

  return handleMerge;
};

export default useHandleMerge;
